import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getProducts, Product } from '../api/products.api';
import LoadingSpinner from '../components/LoadingSpinner';
import ProductImage from '../components/ProductImage';

const JerseysPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // When category filter is active, fetch all products to filter client-side
      // This ensures accurate filtering even if backend doesn't match correctly
      const filters: any = {
        page: categoryFilter ? 1 : currentPage, // Start from page 1 when filtering
        limit: categoryFilter ? 1000 : 25, // Fetch many when filtering, normal pagination otherwise
        is_active: true, // Only show active products
      };

      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      // Try sending category to backend, but we'll also filter client-side as fallback
      if (categoryFilter) {
        filters.category = decodeURIComponent(categoryFilter);
      }

      const response = await getProducts(filters);
      // Response structure: response is ApiResponse<ProductsResponse>
      // response.data is ProductsResponse which contains { data: Product[], pagination: {...} }
      const productsResponse = response.data;
      let allProducts = productsResponse.data || [];
      
      // Client-side filtering by category (ensures accuracy)
      if (categoryFilter) {
        const decodedCategory = decodeURIComponent(categoryFilter).toLowerCase().trim();
        allProducts = allProducts.filter((product) => {
          const categoryName = product.category?.name?.toLowerCase().trim() || '';
          const categorySlug = product.category?.slug?.toLowerCase().trim() || '';
          // Exact match or contains match for category name
          return categoryName === decodedCategory || 
                 categorySlug === decodedCategory ||
                 categoryName.includes(decodedCategory) ||
                 decodedCategory.includes(categoryName);
        });
        
        // Client-side pagination for filtered results
        const itemsPerPage = 25;
        const totalFiltered = allProducts.length;
        const totalPagesFiltered = Math.ceil(totalFiltered / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedProducts = allProducts.slice(startIndex, endIndex);
        
        setProducts(paginatedProducts);
        setTotalPages(totalPagesFiltered || 1);
        setTotalProducts(totalFiltered);
      } else {
        // No category filter - use backend pagination
        setProducts(allProducts);
        setTotalPages(productsResponse.pagination?.total_pages || 1);
        setTotalProducts(productsResponse.pagination?.total || 0);
      }
    } catch (err: any) {
      console.error('Error fetching products:', err);
      setError(err.response?.data?.message || 'Failed to load products. Please try again.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, categoryFilter]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Reset to page 1 when search or category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  if (loading && products.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Home
        </Link>
      </div>
      {categoryFilter && (
        <div className="mb-3">
          <Link to="/jerseys" className="btn btn-outline-secondary btn-sm mb-2">
            <i className="bi bi-arrow-left me-1"></i>
            View All Products
          </Link>
          <div className="alert mb-0" style={{ backgroundColor: '#047857', borderColor: '#059669', color: '#ffffff' }}>
            <i className="bi bi-funnel me-2"></i>
            Showing products for: <strong>{decodeURIComponent(categoryFilter)}</strong>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-3">
        <div>
          <h1 className="h3 section-title">Jerseys Collection</h1>
          <p className="text-muted-soft mb-0">
            {categoryFilter 
              ? `Products in ${decodeURIComponent(categoryFilter)}`
              : 'Browse the latest cricket-ready kits.'}
          </p>
        </div>
        <div className="w-100 w-md-auto" style={{ maxWidth: '400px' }}>
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, category, price, or size..."
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={(e) => e.key === 'Enter' && fetchProducts()}
              aria-label="Search jerseys"
            />
            {searchQuery && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={handleClearSearch}
                title="Clear search"
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="mt-2 small text-muted-soft">
              Found {totalProducts} result{totalProducts !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {products.length === 0 ? (
        <div className="alert alert-secondary text-center">
          <i className="bi bi-search me-2"></i>
          {searchQuery 
            ? `No products found matching "${searchQuery}". Try a different search term.`
            : 'No products available at the moment.'}
        </div>
      ) : (
        <>
          <div className="row g-4">
            {products.map((product) => (
              <div className="col-12 col-md-6 col-lg-3" key={product.id}>
                <div className="card card-surface h-100">
                  <ProductImage
                    src={product.image_url}
                    alt={product.name}
                    height={250}
                  />
                  <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="badge bg-success-subtle text-success">
                        {product.category?.name || 'Uncategorized'}
                      </span>
                      <span className="text-muted-soft fw-semibold">BDT {product.price.toFixed(2)}</span>
                    </div>
                    <h6 className="mb-2">{product.name}</h6>
                    <p className="text-muted-soft small mb-0">
                      Sizes: {product.available_sizes.join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <nav className="mt-4" aria-label="Products pagination">
              <ul className="pagination justify-content-center flex-wrap gap-1">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    type="button" 
                    className="page-link" 
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {Array.from({ length: Math.min(totalPages, 10) }, (_, index) => {
                  const page = index + 1;
                  return (
                    <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                      <button type="button" className="page-link" onClick={() => handlePageChange(page)}>
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    type="button" 
                    className="page-link" 
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default JerseysPage;
